import { useState } from 'react';
import { Stack, Card, Text, Button, Badge, Group, SimpleGrid } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, EmptyState, DataTable, MetricCard } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useAffiliate, useAffiliateWithdrawals, useCancelWithdrawal } from '../../../data/useAffiliate';
import { PayoutAccountCard } from '../components/PayoutAccountCard';
import { WithdrawDrawer } from '../components/WithdrawDrawer';
import { formatMinor, formatDateTime } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { useT } from '../../../i18n/I18nProvider';
import '../refer.css';

/**
 * Getting the money out, and the record of every time it happened.
 *
 * One request at a time, on purpose: it is the same rule the server enforces,
 * and it means the bank account cannot change under a payment somebody is
 * about to make.
 */
export default function ReferWithdrawalsPage() {
  const t = useT();
  const me = useAffiliate();
  const withdrawals = useAffiliateWithdrawals();
  const cancel = useCancelWithdrawal();
  const [open, setOpen] = useState(false);

  if (!me.isLoading && !me.data) {
    return (
      <Stack gap="lg">
        <PageHeader eyebrow={t('refer.eyebrow')} title={t('refer.withdrawals.title')} />
        <EmptyState title={t('refer.notEnrolled.title')} description={t('refer.notEnrolled.body')} />
      </Stack>
    );
  }

  const a = me.data;
  const rows = (withdrawals.data?.data ?? []).map((w, i) => ({ ...w, _i: i }));

  function confirmCancel(id: string, amount: number) {
    openConfirmModal({
      title: t('refer.withdrawals.cancelTitle'),
      children: <Text>{t('refer.withdrawals.cancelBody', { amount: formatMinor(amount) })}</Text>,
      labels: { confirm: t('refer.withdrawals.cancelConfirm'), cancel: t('action.keep') },
      onConfirm: async () => {
        try {
          await cancel.mutateAsync(id);
          notifySuccess(t('refer.withdrawals.cancelled'));
        } catch {
          notifyError(t('refer.withdrawals.cancelFailed'));
        }
      }
    });
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('refer.eyebrow')}
        title={t('refer.withdrawals.title')}
        description={t('refer.withdrawals.description')}
        actions={
          <Button onClick={() => setOpen(true)} disabled={!a || a.balances.available_minor <= 0 || a.has_open_withdrawal}>
            {t('refer.withdraw.open')}
          </Button>
        }
      />

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <MetricCard label={t('refer.metric.available')} value={formatMinor(a?.balances.available_minor ?? 0)} hint={t('refer.metric.availableHint')} />
        <MetricCard label={t('refer.metric.withdrawn')} value={formatMinor(a?.balances.withdrawn_minor ?? 0)} hint={t('refer.metric.withdrawnHint')} />
      </SimpleGrid>

      {a?.has_open_withdrawal && <Alert color="info">{t('refer.withdrawals.oneAtATime')}</Alert>}

      <PayoutAccountCard locked={Boolean(a?.has_open_withdrawal)} />

      <Card withBorder>
        <Stack gap="md">
          <Text role="label">{t('refer.withdrawals.historyTitle')}</Text>
          {withdrawals.isLoading ? (
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('common.loading')}</Text>
          ) : withdrawals.isError ? (
            <Alert color="error" role="alert">
              {t('refer.withdrawals.loadFailed')}{' '}
              <Button variant="subtle" size="sm" onClick={() => void withdrawals.refetch()}>{t('action.retry')}</Button>
            </Alert>
          ) : rows.length === 0 ? (
            <EmptyState title={t('refer.withdrawals.emptyTitle')} description={t('refer.withdrawals.emptyBody')} />
          ) : (
            <DataTable<Indexed<(typeof rows)[number]>>
              idAccessor="_i"
              records={rows}
              columns={[
                { accessor: 'requested_at', title: t('refer.column.requested'), render: (r) => formatDateTime(r.requested_at) },
                { accessor: 'amount_minor', title: t('refer.column.amount'), render: (r) => <span className="rr-refer-amount">{formatMinor(r.amount_minor)}</span> },
                { accessor: 'method', title: t('refer.column.method'), render: (r) => t(`refer.method.${r.method}`, r.method) },
                { accessor: 'status', title: t('refer.column.status'), render: (r) => <Badge>{t(`refer.withdrawalStatus.${r.status}`, r.status)}</Badge> },
                { accessor: 'payment_reference', title: t('refer.column.reference'), render: (r) => r.payment_reference ?? (r.review_note ?? '—') },
                {
                  accessor: '_i',
                  title: '',
                  render: (r) => r.status === 'requested'
                    ? <Button variant="subtle" size="sm" onClick={() => confirmCancel(r.id, r.amount_minor)}>{t('action.cancel')}</Button>
                    : null
                }
              ]}
            />
          )}
        </Stack>
      </Card>

      <WithdrawDrawer
        opened={open}
        onClose={() => setOpen(false)}
        availableMinor={a?.balances.available_minor ?? 0}
        minimumMinor={a?.programme.min_withdrawal_minor ?? 50000}
        hasAccount={Boolean(a?.has_payout_account)}
      />
    </Stack>
  );
}
